/**
 * ConversationAgentV2.js
 *
 * A JSON-driven AI conversation agent that generates options and processes
 * free text input based on the flow configuration.
 *
 * All prompts and templates are read from the JSON config file.
 */

import conversationFlowService from './conversationFlowService';
import { COPY_STYLE_GUIDE } from './copyStyleGuide';

const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY;
const GEMINI_MODEL = import.meta.env.VITE_GEMINI_MODEL || 'gemini-2.5-flash';
const GEMINI_API_URL = `https://generativelanguage.googleapis.com/v1beta/models/${GEMINI_MODEL}:generateContent`;

/**
 * Marketing Pillar Context Builder
 * Provides pillar-specific guidance to the AI for content creation
 */
function buildPillarContextPrompt(pillarContext) {
  if (!pillarContext?.pillar) return '';

  const pillar = pillarContext.pillar;

  let prompt = `
## MARKETING PILLAR CONTEXT
You are creating content for the "${pillar.name}" pillar.

**Pillar Description:** ${pillar.description || 'Not specified'}

**Target Awareness Stages:**
`;

  // Add awareness stage context
  const awarenessMapping = pillar.awarenessMapping || {};
  const activeAwarenessStages = Object.entries(awarenessMapping)
    .filter(([, config]) => config?.recommended)
    .map(([stage, config]) => ({ stage, weight: config.weight }));

  if (activeAwarenessStages.length > 0) {
    activeAwarenessStages.forEach(({ stage, weight }) => {
      const stageDescriptions = {
        unaware: "Doesn't know they have a problem - use entertainment, lifestyle content",
        problemAware: "Knows the problem, not solutions - use educational content, pain points",
        solutionAware: "Knows solutions exist, not your brand - use comparisons, how-to",
        productAware: "Knows your product, evaluating - use social proof, features",
        mostAware: "Ready to buy, needs final push - use offers, urgency"
      };
      prompt += `- ${stage} (weight: ${weight}%): ${stageDescriptions[stage] || stage}\n`;
    });
  }

  // Add content guidelines
  const guidelines = pillar.contentGuidelines || {};

  if (guidelines.toneOfVoice?.length > 0) {
    prompt += `\n**Tone of Voice:** ${guidelines.toneOfVoice.join(', ')}`;
  }

  if (guidelines.keyMessages?.length > 0) {
    prompt += `\n**Key Messages to Convey:**\n${guidelines.keyMessages.map(m => `- ${m}`).join('\n')}`;
  }

  if (guidelines.exampleTopics?.length > 0) {
    prompt += `\n**Example Topics/Themes:**\n${guidelines.exampleTopics.slice(0, 3).map(t => `- ${t}`).join('\n')}`;
  }

  // Add funnel stage context if pillar has funnel mapping
  const funnelMapping = pillar.funnelMapping || {};
  const activeFunnelStages = Object.entries(funnelMapping)
    .filter(([, config]) => config?.recommended)
    .map(([stage]) => stage);

  if (activeFunnelStages.length > 0) {
    prompt += `\n\n**Recommended Funnel Stages:** ${activeFunnelStages.join(', ')}`;
  }

  prompt += `\n\n**IMPORTANT:** Ensure all creative suggestions align with the "${pillar.name}" pillar's tone and messaging guidelines above.\n`;

  return prompt;
}

/**
 * Funnel Stage Context Definitions
 * Provides explicit guidance to the AI for each funnel stage
 */
const FUNNEL_STAGE_CONTEXT = {
  TOFU: {
    name: 'Top of Funnel (Awareness)',
    audience: 'Cold - they don\'t know your brand or may not even recognize they have a problem',
    goal: 'Capture attention, create problem awareness, spark curiosity',
    creativeApproach: {
      headlines: 'Problem-focused, curiosity-driven, relatable pain points',
      visuals: 'Broad appeal, emotionally resonant, pattern-interrupting',
      copy: 'Educational, non-salesy, focuses on the problem not the solution',
      tone: 'Approachable, empathetic, curiosity-inducing'
    },
    ctaDirection: ['Learn More', 'Discover', 'See How', 'Find Out'],
    avoid: ['Hard sells', 'Pricing', 'Feature lists', 'Brand-heavy messaging'],
    examples: [
      'Hook: "Still doing X the hard way?"',
      'Angle: Pain point → hint at solution existing'
    ]
  },

  MOFU: {
    name: 'Middle of Funnel (Consideration)',
    audience: 'Warm - they know the problem exists and are evaluating solutions',
    goal: 'Differentiate from competitors, build preference, demonstrate value',
    creativeApproach: {
      headlines: 'Benefit-focused, comparative, outcome-oriented',
      visuals: 'Product in context, social proof, before/after',
      copy: 'Value propositions, differentiators, how it works',
      tone: 'Confident, informative, trust-building'
    },
    ctaDirection: ['See How It Works', 'Compare', 'Get Demo', 'Watch Video'],
    avoid: ['Generic claims', 'Ignoring competitors exist', 'Too much urgency'],
    buildOn: 'Reference the problem established in TOFU, now position your solution'
  },

  BOFU: {
    name: 'Bottom of Funnel (Conversion)',
    audience: 'Hot - they want to buy, just need final push or reassurance',
    goal: 'Convert, overcome final objections, reduce friction',
    creativeApproach: {
      headlines: 'Action-oriented, urgency, specific offers',
      visuals: 'Product focus, testimonials, trust badges, guarantees',
      copy: 'Objection handling, risk reversal, social proof, scarcity',
      tone: 'Direct, confident, reassuring, urgent but not pushy'
    },
    ctaDirection: ['Buy Now', 'Start Free Trial', 'Get Started', 'Claim Offer'],
    avoid: ['Re-explaining the problem', 'Vague benefits', 'Weak CTAs'],
    buildOn: 'They\'ve seen TOFU/MOFU - now give them reasons to act TODAY'
  }
};

/**
 * Build funnel context prompt for AI
 * Provides explicit stage-specific guidance
 */
function buildFunnelContextPrompt(funnelContext) {
  if (!funnelContext?.funnelStage) return '';

  const stage = FUNNEL_STAGE_CONTEXT[funnelContext.funnelStage];
  if (!stage) return '';

  let prompt = `
## FUNNEL STAGE CONTEXT
You are creating a ${stage.name} creative.

**Audience:** ${stage.audience}

**Your Goal:** ${stage.goal}

**Creative Direction:**
- Headlines: ${stage.creativeApproach.headlines}
- Visuals: ${stage.creativeApproach.visuals}
- Copy: ${stage.creativeApproach.copy}
- Tone: ${stage.creativeApproach.tone}

**CTA Direction:** Use CTAs like: ${stage.ctaDirection.join(', ')}

**Avoid:** ${stage.avoid.join(', ')}
`;

  if (stage.buildOn) {
    prompt += `\n**Build on previous stages:** ${stage.buildOn}\n`;
  }

  if (stage.examples) {
    prompt += `\n**Examples:**\n${stage.examples.map(e => `- ${e}`).join('\n')}\n`;
  }

  // Add previous creatives context if available
  if (funnelContext.previousStageCreatives?.length > 0) {
    prompt += `
**Previous Creatives in Funnel:**
The user has already created these creatives for earlier stages:
${funnelContext.previousStageCreatives.map(c => `- ${c.stage}: "${c.headline}" (${c.angle})`).join('\n')}

Ensure continuity - reference established themes but evolve the message for this stage.
`;
  }

  // Add campaign context if available
  if (funnelContext.campaignName || funnelContext.campaignObjective) {
    prompt += `
**Campaign Context:**
${funnelContext.campaignName ? `- Campaign: ${funnelContext.campaignName}` : ''}
${funnelContext.campaignObjective ? `- Objective: ${funnelContext.campaignObjective}` : ''}
`;
  }

  return prompt;
}

export class ConversationAgentV2 {
  constructor(brand = null, creativeBrief = {}, funnelContext = null, pillarContext = null) {
    this.brand = brand;
    this.creativeBrief = creativeBrief;
    this.funnelContext = funnelContext; // { funnelId, funnelStage, campaignId, campaignName, campaignObjective, previousStageCreatives }
    this.pillarContext = pillarContext; // { pillar: { id, name, description, awarenessMapping, funnelMapping, contentGuidelines } }
    this.conversationHistory = [];
    this.flowConfig = conversationFlowService.getFlowData();
    this.aiPrompts = conversationFlowService.getAIPrompts();
    this.strategistPhaseComplete = false; // Track if strategist phase has been completed
  }

  /**
   * Update the agent's context
   */
  updateContext(brand, creativeBrief, funnelContext = null) {
    this.brand = brand;
    this.creativeBrief = creativeBrief;
    if (funnelContext !== null) {
      this.funnelContext = funnelContext;
    }
  }

  /**
   * Set funnel context
   */
  setFunnelContext(funnelContext) {
    this.funnelContext = funnelContext;
    console.log('[ConversationAgentV2] Funnel context set:', funnelContext);
  }

  /**
   * Get current funnel context
   */
  getFunnelContext() {
    return this.funnelContext;
  }

  /**
   * Set pillar context
   */
  setPillarContext(pillarContext) {
    this.pillarContext = pillarContext;
    console.log('[ConversationAgentV2] Pillar context set:', pillarContext?.pillar?.name);
  }

  /**
   * Get current pillar context
   */
  getPillarContext() {
    return this.pillarContext;
  }

  /**
   * Add a message to conversation history
   */
  addToHistory(role, content) {
    this.conversationHistory.push({ role, content, timestamp: Date.now() });
    // Keep history manageable
    if (this.conversationHistory.length > 50) {
      this.conversationHistory = this.conversationHistory.slice(-30);
    }
  }

  /**
   * Truncate conversation history to a specific length
   * Used when rewinding/deleting messages to sync agent memory with UI state
   * @param {number} toLength - Keep history up to this length
   */
  truncateHistory(toLength) {
    if (toLength <= 0) {
      this.conversationHistory = [];
    } else {
      this.conversationHistory = this.conversationHistory.slice(0, toLength);
    }
    console.log('[ConversationAgentV2] History truncated to length', toLength);
  }

  /**
   * Reset conversation history entirely
   * Used when starting fresh or rewinding to the beginning
   */
  resetHistory() {
    this.conversationHistory = [];
    this.strategistPhaseComplete = false;
    console.log('[ConversationAgentV2] History reset');
  }

  /**
   * Mark the strategist phase as complete
   * After this, brand-level context will no longer be passed to the AI
   */
  markStrategistComplete() {
    this.strategistPhaseComplete = true;
    console.log('[ConversationAgentV2] Strategist phase marked complete - brand context will be excluded from future prompts');
  }

  /**
   * Check if strategist phase is complete
   */
  isStrategistComplete() {
    return this.strategistPhaseComplete;
  }

  /**
   * Reset strategist phase completion status
   * Used when rewinding to before the strategist phase was complete
   */
  resetStrategistComplete() {
    this.strategistPhaseComplete = false;
    console.log('[ConversationAgentV2] Strategist phase completion reset');
  }

  /**
   * Get current conversation history length
   * Used to track the history index in state snapshots
   */
  getHistoryLength() {
    return this.conversationHistory.length;
  }

  /**
   * Generate options for a step using AI
   * @param {Object} params
   * @param {string} params.prompt - The prompt template
   * @param {string} params.phaseId - Current phase ID
   * @param {number} params.stepIndex - Current step index
   * @param {string} params.field - The brief field being populated
   * @param {Object} params.creativeBrief - Current creative brief
   * @param {Object} params.brand - Brand information
   * @param {Array} params.previousOptions - Previously shown options to avoid repeating
   */
  async generateOptions({ prompt, phaseId, stepIndex, field, creativeBrief, brand, previousOptions = [] }) {
    const step = conversationFlowService.getPhaseStep(phaseId, stepIndex);
    if (!step) {
      throw new Error(`Step ${stepIndex} not found in phase ${phaseId}`);
    }

    // Build the full prompt
    const systemPrompt = this.buildSystemPrompt(phaseId, creativeBrief, brand);
    const contextPrompt = this.buildContextPrompt(phaseId, creativeBrief, brand);

    // Build previous options context if regenerating
    const previousOptionsContext = previousOptions.length > 0
      ? `\n\nIMPORTANT: The user asked for MORE options. Do NOT repeat these previously shown options:\n${previousOptions.map(o => `- ${o.text || o}`).join('\n')}\n\nGenerate completely NEW and DIFFERENT options.`
      : '';

    const fullPrompt = `${systemPrompt}

${contextPrompt}

${prompt}
${previousOptionsContext}

IMPORTANT: Respond with valid JSON in this exact format:
{
  "message": "Your conversational message to the user",
  "options": [
    {"text": "Option 1 display text", "value": "Option 1 full text value", "briefKey": "${field}"},
    {"text": "Option 2 display text", "value": "Option 2 full text value", "briefKey": "${field}"},
    {"text": "Option 3 display text", "value": "Option 3 full text value", "briefKey": "${field}"}
  ]
}

Generate 3-4 contextually relevant options. Each option should have:
- text: What the user sees (conversational, specific)
- value: The full text content to be saved to the database. IMPORTANT: For creative fields (like angles, headlines, concepts), this MUST be the full descriptive text, NOT a slug or ID.
- briefKey: "${field}"

${COPY_STYLE_GUIDE}`;

    console.log('[ConversationAgentV2] Full prompt sent to AI:', fullPrompt);

    try {
      const response = await this.callGeminiAPI(fullPrompt);
      return this.parseJSONResponse(response, field);
    } catch (error) {
      console.error('[ConversationAgentV2] Error generating options:', error);
      // Return fallback from JSON config
      const fallback = step.optionsGeneration?.fallback;
      if (fallback) {
        return {
          message: fallback.message || step.message || step.greeting?.message,
          options: Array.isArray(fallback) ? fallback.map(text => ({
            text,
            value: text,
            briefKey: field
          })) : fallback.options
        };
      }
      throw error;
    }
  }

  /**
   * Handle "Help me decide" brainstorming
   */
  async brainstorm({ phaseId, stepIndex, creativeBrief, brand }) {
    const step = conversationFlowService.getPhaseStep(phaseId, stepIndex);
    const phase = conversationFlowService.getPhase(phaseId);
    const field = step?.field;

    const persona = phase?.persona || { name: 'Assistant', role: 'Creative Helper' };

    const systemPrompt = this.buildSystemPrompt(phaseId, creativeBrief, brand);
    const contextPrompt = this.buildContextPrompt(phaseId, creativeBrief, brand);

    const prompt = `${systemPrompt}

${contextPrompt}

The user clicked "Help me decide" for the ${field} field.

As ${persona.name} the ${persona.role}, help them decide by asking ONE clarifying question.

Rules:
- Ask ONE specific question to help narrow down the options
- ALWAYS provide 2-4 clickable answer options for your question
- Each option should be a direct answer to your question
- Be conversational, not robotic
- The options should help guide them toward a decision

IMPORTANT: You MUST include options array with clickable choices. Never ask a question without providing options to answer it.

Respond with JSON:
{
  "message": "Your single clarifying question",
  "options": [
    {"text": "Answer choice 1", "value": "choice1", "briefKey": "${field}"},
    {"text": "Answer choice 2", "value": "choice2", "briefKey": "${field}"},
    {"text": "Answer choice 3", "value": "choice3", "briefKey": "${field}"}
  ]
}`;

    try {
      const response = await this.callGeminiAPI(prompt);
      const result = this.parseJSONResponse(response, field);

      // Ensure all options have the briefKey for the current field
      if (result.options && field) {
        result.options = result.options.map(opt => ({
          ...opt,
          briefKey: opt.briefKey || field
        }));
      }

      // Always add a "Still not sure" option to continue brainstorming
      if (result.options) {
        result.options.push({
          text: "💡 Still not sure - ask me another question",
          value: "help_brainstorm",
          action: "brainstorm"
        });
      }

      return result;
    } catch (error) {
      console.error('[ConversationAgentV2] Error in brainstorm:', error);
      return {
        message: "Let me help you think through this. What's most important to you - reaching the widest audience, or connecting deeply with a specific group?",
        options: [
          { text: "Widest reach possible", value: "wide_reach", briefKey: field },
          { text: "Deep connection with specific group", value: "specific_group", briefKey: field },
          { text: "💡 Ask me a different question", value: "help_brainstorm", action: "brainstorm" }
        ]
      };
    }
  }

  /**
   * Brainstorm more audience options
   */
  async brainstormAudiences({ creativeBrief, brand, previousAudiences = [] }) {
    const audienceConfig = conversationFlowService.getAudienceConfirmationConfig();

    const systemPrompt = this.buildSystemPrompt('strategist', creativeBrief, brand);
    const contextPrompt = this.buildContextPrompt(phaseId, creativeBrief, brand);

    const prompt = `${systemPrompt}

${contextPrompt}

The user wants more target audience options.

Previously suggested audiences (do NOT repeat these):
${previousAudiences.map(a => `- ${a}`).join('\n') || 'None yet'}

Generate 4 NEW, DIFFERENT target audience options that would be relevant for this product/brand.
Be specific - include demographics, psychographics, and behaviors where relevant.

Respond with JSON:
{
  "message": "Your conversational message introducing the new options",
  "audiences": [
    "Detailed audience description 1",
    "Detailed audience description 2",
    "Detailed audience description 3",
    "Detailed audience description 4"
  ]
}`;

    try {
      const response = await this.callGeminiAPI(prompt);
      const parsed = this.parseJSONResponse(response, 'targetAudience');

      // Transform to expected format
      if (parsed.audiences) {
        return parsed;
      }
      if (parsed.options) {
        return {
          message: parsed.message,
          audiences: parsed.options.map(o => o.text || o.value)
        };
      }
      return parsed;
    } catch (error) {
      console.error('[ConversationAgentV2] Error brainstorming audiences:', error);
      return {
        message: "Here are some more audience options to consider:",
        audiences: [
          "Young professionals aged 25-35 seeking lifestyle upgrades",
          "Budget-conscious families looking for quality value",
          "Tech-savvy early adopters eager for innovation",
          "Eco-conscious consumers prioritizing sustainability"
        ]
      };
    }
  }

  /**
   * Process free text input from user
   */
  async processFreeText({ input, phaseId, stepIndex, creativeBrief, brand }) {
    const step = conversationFlowService.getPhaseStep(phaseId, stepIndex);
    const field = step?.field;

    const systemPrompt = this.buildSystemPrompt(phaseId, creativeBrief, brand);
    const contextPrompt = this.buildContextPrompt(phaseId, creativeBrief, brand);

    const prompt = `${systemPrompt}

${contextPrompt}

Current step: ${field}
User input: "${input}"

Analyze this input:
1. Is it an answer to the current question, or a question from the user?
2. If it's an answer:
   - Polish it to be more professional/creative as appropriate for the ${field} field. 
   - Ensure the polished version captures the core meaning of the user's input.
3. If it's a question, answer it helpfully.

Respond with JSON:
{
  "type": "answer" | "question",
  "polishedValue": "the polished version of the answer (if type is answer)",
  "message": "Your response message. If type is answer, acknowledge the input positively.",
  "advance": false
}`;

    try {
      const response = await this.callGeminiAPI(prompt);
      const result = this.parseJSONResponse(response, field);

      // Handle Answer Polishing & Confirmation
      if (result.type === 'answer' && result.polishedValue) {
        // Check if polished value is meaningfully different from original
        // Strip common formatting additions that don't constitute real changes
        const stripFormatting = (str) => str
          .trim()
          .toLowerCase()
          .replace(/["']/g, '')           // Remove quotes
          .replace(/\s*(pixels?|px)\s*/gi, '')  // Remove pixel units
          .replace(/\s+/g, ' ')           // Normalize whitespace
          .replace(/[^\w\d:\/\.\-]/g, '') // Keep only alphanumeric, colons, slashes, dots, dashes
          .trim();

        const normalizedOriginal = stripFormatting(input);
        const normalizedPolished = stripFormatting(result.polishedValue);

        // Also check if polished just contains original with minor additions
        const originalContainsPolished = normalizedOriginal.includes(normalizedPolished);
        const polishedContainsOriginal = normalizedPolished.includes(normalizedOriginal);

        const isPolishMeaningfullyDifferent = normalizedPolished !== normalizedOriginal &&
          !originalContainsPolished &&
          !polishedContainsOriginal;

        if (isPolishMeaningfullyDifferent) {
          // Show polished vs original choice only when there's a real difference
          const confirmationMessage = `${result.message}\n\nI've polished that slightly to:\n\n**"${result.polishedValue}"**\n\nWhich would you prefer to use?`;

          return {
            type: 'answer',
            message: confirmationMessage,
            options: [
              {
                text: "✨ Use Polished Version",
                value: result.polishedValue,
                briefKey: field
              },
              {
                text: "Original",
                value: input,
                briefKey: field
              }
            ],
            advance: false // Wait for user selection
          };
        } else {
          // Polished value is same as original - just accept it and advance
          return {
            type: 'answer',
            briefUpdate: { field, value: input },
            message: result.message || "Got it!",
            advance: true
          };
        }
      } else if (result.type === 'answer') {
        // Fallback if no polished value returned (unlikely with prompt)
        return {
          type: 'answer',
          briefUpdate: { field, value: input },
          message: result.message || "Got it!",
          advance: true
        };
      }

      // Handle Questions/Other
      return {
        ...result,
        advance: false // Don't advance on questions
      };

    } catch (error) {
      console.error('[ConversationAgentV2] Error processing free text:', error);
      // Simple fallback - use input directly
      return {
        type: 'answer',
        briefUpdate: { field, value: input },
        message: `Got it! Using "${input}" for ${field}.`,
        advance: true
      };
    }
  }

  /**
   * Build system prompt based on phase
   * Brand-level context is only included during the strategist phase
   */
  buildSystemPrompt(phaseId, creativeBrief = null, brand = null) {
    const phase = conversationFlowService.getPhase(phaseId);
    const basePrompt = conversationFlowService.getSystemPromptBase();
    const phasePromptTemplate = conversationFlowService.getPhasePromptTemplate(phaseId);
    const philosophy = conversationFlowService.getConversationalPhilosophy();

    const persona = phase?.persona || { name: 'Assistant', role: 'Creative Helper', tone: 'Helpful' };

    // Build brief summary for interpolation
    const briefSummary = this.buildBriefSummary(creativeBrief);

    // Build brand context for interpolation - only include during strategist phase
    let brandContext = '';
    if (phaseId === 'strategist' && brand) {
      brandContext = `Brand: ${brand.name}\nProducts: ${Array.isArray(brand.products) ? brand.products.join(', ') : (brand.products || 'Not specified')}`;
    }

    // Interpolate the phase prompt template with actual values
    // Note: phasePrompt already includes persona info, so we don't duplicate it
    const phasePrompt = phasePromptTemplate
      .replace(/\{briefSummary\}/g, briefSummary || 'No previous conversation yet.')
      .replace(/\{basePrompt\}/g, '') // basePrompt is already included at the top
      .replace(/\{brandContext\}/g, brandContext);

    // Only add persona if the phase template doesn't include it
    const hasPersonaInTemplate = phasePromptTemplate.includes('You are ');
    const personaSection = hasPersonaInTemplate ? '' : `You are ${persona.name}, a ${persona.role}.\n`;

    return `${basePrompt}

${personaSection}${phasePrompt}

Core principle: ${philosophy.corePrinciple || 'Be a helpful creative collaborator'}

Guidelines:
- Share insights and reasoning, don't just ask questions
- Make recommendations based on context
- Be conversational, not robotic
- Keep responses concise but helpful`;
  }

  /**
   * Build a human-readable summary of the creative brief for AI context
   */
  buildBriefSummary(creativeBrief) {
    if (!creativeBrief || Object.keys(creativeBrief).length === 0) {
      return null;
    }

    const summaryParts = [];

    // Strategy fields
    if (creativeBrief.product) {
      summaryParts.push(`Product: ${creativeBrief.product}`);
    }
    if (creativeBrief.targetAudience) {
      const audience = typeof creativeBrief.targetAudience === 'object'
        ? creativeBrief.targetAudience.description || creativeBrief.targetAudience.name || JSON.stringify(creativeBrief.targetAudience)
        : creativeBrief.targetAudience;
      summaryParts.push(`Target Audience: ${audience}`);
    }
    if (creativeBrief.objective) {
      summaryParts.push(`Objective: ${creativeBrief.objective}`);
    }
    if (creativeBrief.desiredEmotion) {
      summaryParts.push(`Desired Emotion: ${creativeBrief.desiredEmotion}`);
    }
    if (creativeBrief.geography) {
      summaryParts.push(`Geography: ${creativeBrief.geography}`);
    }

    // Creative direction fields
    if (creativeBrief.marketingAngle) {
      summaryParts.push(`Marketing Angle: ${creativeBrief.marketingAngle}`);
    }
    if (creativeBrief.differentiators) {
      summaryParts.push(`Key Differentiators: ${creativeBrief.differentiators}`);
    }

    // Copy fields
    if (creativeBrief.selectedHeadline) {
      summaryParts.push(`Headline: ${creativeBrief.selectedHeadline}`);
    }
    if (creativeBrief.tone) {
      summaryParts.push(`Tone: ${creativeBrief.tone}`);
    }
    if (creativeBrief.cta) {
      summaryParts.push(`Call to Action: ${creativeBrief.cta}`);
    }

    // Visual direction fields
    if (creativeBrief.visualStyle) {
      summaryParts.push(`Visual Style: ${creativeBrief.visualStyle}`);
    }
    if (creativeBrief.imageType) {
      summaryParts.push(`Image Type: ${creativeBrief.imageType}`);
    }
    if (creativeBrief.photographyMood) {
      summaryParts.push(`Photography Mood: ${creativeBrief.photographyMood}`);
    }
    if (creativeBrief.peopleDescription) {
      summaryParts.push(`People Description: ${creativeBrief.peopleDescription}`);
    }

    // Casting and location (extract description from objects)
    if (creativeBrief.selectedCasting) {
      const casting = typeof creativeBrief.selectedCasting === 'object'
        ? creativeBrief.selectedCasting.description || creativeBrief.selectedCasting.title || JSON.stringify(creativeBrief.selectedCasting)
        : creativeBrief.selectedCasting;
      summaryParts.push(`Selected Casting: ${casting}`);
    }
    if (creativeBrief.selectedLocation) {
      const location = typeof creativeBrief.selectedLocation === 'object'
        ? creativeBrief.selectedLocation.description || creativeBrief.selectedLocation.title || JSON.stringify(creativeBrief.selectedLocation)
        : creativeBrief.selectedLocation;
      summaryParts.push(`Selected Location: ${location}`);
    }

    return summaryParts.join('\n');
  }

  /**
   * Build context prompt with current brief and brand info
   * Only includes summary-relevant fields (not intermediate data like crawledImages, etc.)
   * Brand-level context is only included during the strategist phase
   * Funnel context is included when available to provide stage-specific guidance
   */
  buildContextPrompt(phaseId, creativeBrief, brand) {
    // Only include fields that are relevant for summary/context
    // Exclude intermediate fields like crawledImages, urlSummaryForAudience, etc.
    const summaryFields = [
      'product', 'targetAudience', 'desiredEmotion', 'objective', 'geography',
      'creativeRiskTolerance', 'marketingAngle', 'differentiators',
      'selectedHeadline', 'tone', 'copyStyle', 'cta', 'legalRequirements',
      'visualStyle', 'imageType', 'sceneType', 'photographyMood',
      'peopleDescription', 'selectedCasting', 'selectedLocation',
      'brandColors', 'outputType'
    ];

    const briefSummary = Object.entries(creativeBrief || {})
      .filter(([k, v]) => summaryFields.includes(k) && v && (typeof v !== 'object' || Object.keys(v).length > 0))
      .map(([k, v]) => `- ${k}: ${this.formatValue(v)}`)
      .join('\n');

    console.log('[ConversationAgentV2] Building context prompt with briefSummary:', briefSummary);

    // Build funnel context if available
    const funnelContextPrompt = buildFunnelContextPrompt(this.funnelContext);
    if (funnelContextPrompt) {
      console.log('[ConversationAgentV2] Including funnel context for stage:', this.funnelContext?.funnelStage);
    }

    // Build pillar context if available
    const pillarContextPrompt = buildPillarContextPrompt(this.pillarContext);
    if (pillarContextPrompt) {
      console.log('[ConversationAgentV2] Including pillar context for:', this.pillarContext?.pillar?.name);
    }

    // Only include brand-level context during strategist phase
    // All other phases use only the creative brief data captured in the conversation
    if (phaseId !== 'strategist') {
      console.log('[ConversationAgentV2] Non-strategist phase - excluding brand context from prompt');
      return `Current creative brief:
${briefSummary || 'No fields collected yet'}
${pillarContextPrompt}
${funnelContextPrompt}`;
    }

    // Format brand audience properly (can be string or object)
    const formatAudience = (audience) => {
      if (!audience) return 'Not specified';
      if (typeof audience === 'string') return audience;
      if (typeof audience === 'object') {
        return audience.description || audience.name || JSON.stringify(audience);
      }
      return String(audience);
    };

    // Get colors from visual object (primaryColor, secondaryColor, accentColor)
    const formatBrandColors = (brand) => {
      const visual = brand?.visual || {};
      const colors = [visual.primaryColor, visual.secondaryColor, visual.accentColor].filter(Boolean);
      return colors.length > 0 ? colors.join(', ') : 'Not specified';
    };

    const brandContext = brand ? `
Brand: ${brand.name}
Products: ${Array.isArray(brand.products) ? brand.products.join(', ') : (brand.products || 'Not specified')}
Brand colors: ${formatBrandColors(brand)}
Target audience: ${formatAudience(brand.audience)}` : '';

    return `Current creative brief:
${briefSummary || 'No fields collected yet'}

${brandContext}
${pillarContextPrompt}
${funnelContextPrompt}`;
  }

  /**
   * Format a value for display in prompts (handles objects, arrays, etc.)
   */
  formatValue(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (Array.isArray(value)) {
      return value.map(v => this.formatValue(v)).join(', ');
    }
    if (typeof value === 'object') {
      // Try to extract a meaningful string representation
      if (value.description) return value.description;
      if (value.name) return value.name;
      if (value.text) return value.text;
      if (value.title) return value.title;
      // Fall back to JSON but keep it concise for common fields
      return JSON.stringify(value);
    }
    return String(value);
  }

  /**
   * Call Gemini API
   */
  async callGeminiAPI(prompt, options = {}) {
    if (!GEMINI_API_KEY) {
      throw new Error('Gemini API key not configured');
    }

    const { maxOutputTokens = 1024, temperature = 0.7 } = options;

    const response = await fetch(`${GEMINI_API_URL}?key=${GEMINI_API_KEY}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{
          parts: [{ text: prompt }]
        }],
        generationConfig: {
          temperature,
          maxOutputTokens
        }
      })
    });

    if (!response.ok) {
      const error = await response.text();
      console.error('[ConversationAgentV2] Gemini API error:', error);
      throw new Error(`Gemini API error: ${error}`);
    }

    const data = await response.json();
    return data.candidates?.[0]?.content?.parts?.[0]?.text || '';
  }

  /**
   * Parse JSON from AI response
   */
  parseJSONResponse(text, defaultBriefKey = null) {
    // Extract JSON from response
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      console.error('[ConversationAgentV2] No JSON found in response:', text);
      throw new Error('No JSON found in response');
    }

    const parsed = JSON.parse(jsonMatch[0]);

    // Ensure options have briefKey if not specified
    if (parsed.options && defaultBriefKey) {
      parsed.options = parsed.options.map(opt => ({
        ...opt,
        briefKey: opt.briefKey || defaultBriefKey
      }));
    }

    return parsed;
  }

  /**
   * Generate headlines using AI
   */
  async generateHeadlines(creativeBrief, brand) {
    return this.generateOptions({
      prompt: `Generate 3-4 compelling headline options for this advertisement.
Each headline should be punchy, memorable, and aligned with the marketing angle.
Consider the target audience and desired emotion.`,
      phaseId: 'copywriter',
      stepIndex: 0,
      field: 'selectedHeadline',
      creativeBrief,
      brand
    });
  }

  /**
   * Generate CTA options using AI
   */
  async generateCTAOptions(creativeBrief, brand) {
    return this.generateOptions({
      prompt: `Generate 3-4 call-to-action (CTA) options for this advertisement.
Each CTA should be action-oriented and aligned with the campaign objective.
Consider what would motivate the target audience to act.`,
      phaseId: 'copywriter',
      stepIndex: 3,
      field: 'cta',
      creativeBrief,
      brand
    });
  }

  /**
   * Generate marketing angle options using AI
   */
  async generateMarketingAngles(creativeBrief, brand) {
    // Get the boldness level to inform marketing angle suggestions
    const boldnessLevel = creativeBrief.creativeRiskTolerance || 'balanced';

    return this.generateOptions({
      prompt: `Generate 3-4 marketing angle options for this creative campaign.
The client wants a ${boldnessLevel} approach to creativity.
Each angle should offer a distinct strategic direction that matches this boldness level.
Consider what would resonate most with the target audience.`,
      phaseId: 'creative_director',
      stepIndex: 1,
      field: 'marketingAngle',
      creativeBrief,
      brand
    });
  }

  /**
   * Generate image concept options using AI
   */
  async generateImageConcepts(creativeBrief, brand) {
    return this.generateOptions({
      prompt: `Generate 3-4 visual concept options for this advertisement.
Each concept should paint a vivid picture of what the ad will look like.
Consider the visual style, image type, and brand aesthetic.`,
      phaseId: 'art_director',
      stepIndex: 4,
      field: 'imageConcept',
      creativeBrief,
      brand
    });
  }

  /**
   * Infer target audience from URL content
   */
  async inferAudienceFromURL(urlSummary, brand) {
    const prompt = `${this.buildSystemPrompt('strategist', null, brand)}

Analyze this page content to infer the target audience:

Title: ${urlSummary.title || 'N/A'}
Description: ${urlSummary.description || 'N/A'}
Headline: ${urlSummary.headline || 'N/A'}

Based on this content, suggest:
1. A primary target audience (be specific with demographics and psychographics)
2. Your confidence level (high/medium/low)
3. Brief reasoning
4. 3 alternative audience options

Respond with JSON:
{
  "suggestedAudience": "Detailed primary audience description",
  "confidence": "high" | "medium" | "low",
  "reasoning": "Brief explanation of why this audience makes sense",
  "alternatives": [
    "Alternative audience 1",
    "Alternative audience 2",
    "Alternative audience 3"
  ]
}`;

    try {
      const response = await this.callGeminiAPI(prompt);
      return this.parseJSONResponse(response, 'targetAudience');
    } catch (error) {
      console.error('[ConversationAgentV2] Error inferring audience from URL:', error);
      return null;
    }
  }

  /**
   * Generate brief sheet data from the creative brief
   */
  async generateBriefSheet(creativeBrief) {
    console.log('[ConversationAgentV2] generateBriefSheet received creativeBrief:', creativeBrief);

    // Merge passed creativeBrief with internal state to ensure we have all values
    const mergedBrief = { ...this.creativeBrief, ...creativeBrief };

    // Structure the creative brief data by phase
    const briefData = {
      brandName: this.brand?.name || 'Your Brand',
      outputType: mergedBrief.outputType || 'image',
      strategist: {
        product: mergedBrief.product,
        targetAudience: mergedBrief.targetAudience,
        objective: mergedBrief.objective,
        desiredEmotion: mergedBrief.desiredEmotion,
        geography: mergedBrief.geography
      },
      creativeDirector: {
        marketingAngle: mergedBrief.marketingAngle,
        differentiators: mergedBrief.differentiators
      },
      copywriter: {
        selectedHeadline: mergedBrief.selectedHeadline,
        cta: mergedBrief.cta,
        tone: mergedBrief.tone,
        copyStyle: mergedBrief.copyStyle,
        legalRequirements: mergedBrief.legalRequirements
      },
      artDirector: {
        visualStyle: mergedBrief.visualStyle,
        imageType: mergedBrief.imageType,
        sceneType: mergedBrief.sceneType,
        photographyMood: mergedBrief.photographyMood,
        peopleDescription: mergedBrief.peopleDescription,
        selectedCasting: mergedBrief.selectedCasting,
        selectedLocation: mergedBrief.selectedLocation,
        logo: mergedBrief.logo,
        brandColors: mergedBrief.brandColors,
        focusHierarchy: mergedBrief.focusHierarchy,
        textOverlays: mergedBrief.textOverlays,
        textOverlayDetails: mergedBrief.textOverlayDetails
      },
      assets: {
        logos: this.brand?.logo ? [{
          url: this.brand.logo,
          name: `${this.brand.name || 'Brand'} Logo`,
          type: 'brand',
          useCase: 'logo'
        }] : [],
        productImages: (mergedBrief.selectedAssets || mergedBrief.assets || []).map(asset => ({
          url: asset.url || asset.imageUrl,
          name: asset.name || asset.title || 'Product Image',
          type: asset.type || asset.source || 'upload',
          useCase: asset.useCase || 'incorporate'
        }))
      }
    };

    // Add video settings if this is a video brief
    if (mergedBrief.outputType === 'video' && mergedBrief.videoSettings) {
      briefData.videoSettings = {
        videoLength: mergedBrief.videoSettings.videoLength,
        videoStyle: mergedBrief.videoSettings.videoStyle,
        videoType: mergedBrief.videoSettings.videoType,
        actorCount: mergedBrief.videoSettings.actorCount,
        actorDescription: mergedBrief.videoSettings.actorDescription,
        scriptStyle: mergedBrief.videoSettings.scriptStyle,
        scriptContent: mergedBrief.videoSettings.scriptContent
      };
    }

    // Add still settings if available
    if (mergedBrief.stillSettings) {
      briefData.stillSettings = mergedBrief.stillSettings;
    }

    return briefData;
  }

  /**
   * Generate image description for the brief
   * Note: This is always called after strategist phase, so brand context is excluded
   */
  async generateImageDescription(creativeBrief) {
    const prompt = `Based on this creative brief, write a clear, concise description (2-3 sentences) of what the generated image will look like.

Creative Brief:
${JSON.stringify(creativeBrief, null, 2)}

Write this as if you're describing the final advertisement to someone who can't see it yet. Focus on:
- The main visual elements
- The overall style and mood
- How the headline and CTA will be displayed

Be specific but concise. Return ONLY the description text, nothing else.`;

    try {
      const response = await this.callGeminiAPI(prompt);
      return response.trim();
    } catch (error) {
      console.error('[ConversationAgentV2] Error generating image description:', error);
      throw new Error(`Failed to generate image description: ${error.message}`);
    }
  }

  /**
   * Generate video description for the brief
   * Note: This is always called after strategist phase, so brand context is excluded
   */
  async generateVideoDescription(creativeBrief) {
    const videoSettings = creativeBrief.videoSettings || {};

    const prompt = `Based on this video creative brief, write a clear, concise description (3-4 sentences) of what the generated video ad will look like.

Creative Brief:
${JSON.stringify(creativeBrief, null, 2)}

Video Settings:
- Length: ${videoSettings.videoLength || '6s'}
- Style: ${videoSettings.videoStyle || 'commercial'}
- Type: ${videoSettings.videoType || 'actor_driven'}
${videoSettings.actorCount ? `- Cast: ${videoSettings.actorCount} person(s)` : ''}
${videoSettings.actorDescription ? `- Actors: ${videoSettings.actorDescription}` : ''}
${videoSettings.scriptStyle ? `- Audio: ${videoSettings.scriptStyle}` : ''}

Script:
${videoSettings.scriptContent || 'No script provided'}

Write this as if you're describing the final video advertisement to someone who can't see it yet. Focus on:
- The opening scene and hook
- The main action/story
- The visual style and mood
- How the message and CTA will be delivered
- The closing shot

Be specific but concise. Return ONLY the description text, nothing else.`;

    try {
      const response = await this.callGeminiAPI(prompt);
      return response.trim();
    } catch (error) {
      console.error('[ConversationAgentV2] Error generating video description:', error);
      throw new Error(`Failed to generate video description: ${error.message}`);
    }
  }

  /**
   * Process AI-assisted brief edit
   */
  async processAIBriefEdit(sectionKey, currentText, instructions) {
    const prompt = `You are helping edit a section of a creative brief.

Current content:
${currentText}

User's edit instructions:
${instructions}

Apply the user's requested changes to the content. Keep the same format (field labels with values).
Return ONLY the updated content, nothing else.`;

    try {
      const response = await this.callGeminiAPI(prompt);
      return response.trim();
    } catch (error) {
      console.error('[ConversationAgentV2] Error in AI brief edit:', error);
      return currentText;
    }
  }

  /**
   * Generate 4 distinct casting options for static image ads with people
   * @param {string} peopleDescription - User's description of the people they want
   * @param {object} creativeBrief - Current creative brief
   * @param {object} brand - Brand information
   * @returns {Array} - Array of casting options with image prompts
   */
  async generateCastingPrompts(peopleDescription, creativeBrief, brand) {
    const prompt = `You are a casting director for a static image advertisement. Generate 4 DISTINCT character options for the MAIN PERSON/PEOPLE in this ad.

AD CONTEXT:
- Product: ${creativeBrief.product || 'Not specified'}
- Target Audience: ${creativeBrief.targetAudience || 'Not specified'}
- Marketing Angle: ${creativeBrief.marketingAngle || 'Not specified'}
- Visual Style: ${creativeBrief.visualStyle || 'commercial'}
- Desired Emotion: ${creativeBrief.desiredEmotion || 'Not specified'}
- Scene Type: ${creativeBrief.sceneType || 'lifestyle'}
- Photography Mood: ${creativeBrief.photographyMood || 'natural'}

USER'S PEOPLE DESCRIPTION:
${peopleDescription}

IMPORTANT: Generate 4 casting options that match the user's description but offer variety. Consider:
- Different ages within the target demographic
- Different styles (casual vs professional vs athletic)
- Different energy levels (calm/confident vs energetic/dynamic)
- Different compositions (single person vs group dynamics)
- The character(s) should feel authentic and relatable to the target audience

For each option provide:
1. A character name (first name only, realistic and appropriate for the demographic)
2. A descriptive title that captures their essence
3. A detailed description of the person/people
4. A PURE HEADSHOT image prompt for generating the casting photo

CRITICAL HEADSHOT REQUIREMENTS for imagePrompt:
- ONLY the face and neck visible - cropped tightly from just below the chin to just above the top of the head
- Pure white or light gray seamless studio background - NO environmental elements
- Professional portrait studio lighting (softbox or ring light style)
- Subject looking directly at the camera
- Natural, authentic expression matching the character's personality
- Sharp focus on eyes and facial features
- NO shoulders, NO chest, NO clothing visible - FACE ONLY
- NO props, NO accessories (unless essential like glasses)
- Think passport photo or acting headshot composition
- ABSOLUTELY NO TEXT of any kind - no names, labels, captions, titles, or watermarks
- NO decorations, borders, frames, graphics, or overlays
- NO logos or branding elements
- The image must be a clean, unadorned photograph only

Return as JSON array:
[
  {
    "id": 1,
    "name": "First name (e.g., 'Sarah', 'Marcus', 'Elena')",
    "title": "Short descriptive title (e.g., 'The Confident Professional')",
    "description": "Detailed description of the person - age, ethnicity, facial features, hair, expression, personality they convey",
    "imagePrompt": "Tight headshot portrait of [detailed face description]. Face only from chin to top of head, pure white studio background, professional portrait lighting, looking directly at camera with [expression], sharp focus on eyes and facial features, no shoulders visible, no text, no watermarks, no decorations, clean photograph only."
  }
]`;

    try {
      const response = await this.callGeminiAPI(prompt);
      let text = response.replace(/```json/g, '').replace(/```/g, '').trim();

      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        console.log('[ConversationAgentV2] Generated casting prompts:', parsed);
        return parsed;
      }

      throw new Error('No valid JSON array found in AI response');
    } catch (error) {
      console.error('[ConversationAgentV2] Error generating casting prompts:', error);
      throw error; // Re-throw - no fallbacks
    }
  }

  /**
   * Generate 4 distinct location/scenery options for ad shoots
   * Uses the full creative brief context and conversation history for highly relevant suggestions
   * @param {string} sceneryDescription - User's description of the location/setting (may be just scene type)
   * @param {object} creativeBrief - Current creative brief with all collected information
   * @param {object} brand - Brand information
   * @returns {Array} - Array of location options with image prompts
   */
  async generateLocationPrompts(sceneryDescription, creativeBrief, brand) {
    // Extract key creative decisions for location context
    const marketingAngle = creativeBrief.marketingAngle || '';
    const headline = creativeBrief.selectedHeadline || '';

    const prompt = `Generate 4 location options for an ad shoot. Keep responses CONCISE.

CONTEXT:
- Product: ${creativeBrief.product || 'Not specified'}
- Audience: ${creativeBrief.targetAudience || 'Not specified'}
- Emotion: ${creativeBrief.desiredEmotion || 'Not specified'}
- Concept: ${marketingAngle || 'Not specified'}
- Headline: ${headline || 'Not specified'}
- Scene Type: ${sceneryDescription}
- Visual Style: ${creativeBrief.visualStyle || 'modern'}
- Lighting: ${creativeBrief.photographyMood || 'natural daylight'}

Generate 4 DIFFERENT variations of "${sceneryDescription}" that support the concept and resonate with the audience.

IMPORTANT: Keep descriptions SHORT (2-3 sentences max). Keep imagePrompt under 100 words.

Return ONLY a JSON array, no other text:
[
  {
    "id": 1,
    "name": "Short location type",
    "title": "Atmospheric title",
    "description": "Brief description of space and how it fits the concept.",
    "imagePrompt": "Professional architectural photography, wide establishing shot of [location]. EMPTY SPACE - absolutely no people, no text, no words, no labels, no signage, no watermarks. ${creativeBrief.photographyMood || 'Natural daylight'} lighting. [Key design elements]. Clean cinematic composition."
  }
]`;

    try {
      const response = await this.callGeminiAPI(prompt);
      console.log('[ConversationAgentV2] Raw location prompt response:', response);

      let text = response.replace(/```json/g, '').replace(/```/g, '').trim();

      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        console.log('[ConversationAgentV2] Generated location prompts:', parsed);
        return parsed;
      }

      // If no array found, log the cleaned text for debugging
      console.error('[ConversationAgentV2] Could not find JSON array in response. Cleaned text:', text.substring(0, 500));
      throw new Error('No valid JSON array found in AI response');
    } catch (error) {
      console.error('[ConversationAgentV2] Error generating location prompts:', error);
      throw error;
    }
  }

  /**
   * Generate 4 distinct first frame options for video ads
   * Combines selected casting + location + product into opening shots
   * @param {object} creativeBrief - Current creative brief with selectedCasting and selectedLocation
   * @param {object} brand - Brand information
   * @returns {Array} - Array of first frame options with image prompts
   */
  async generateFirstFramePrompts(creativeBrief, brand) {
    const videoSettings = creativeBrief.videoSettings || {};
    const selectedCasting = creativeBrief.selectedCasting || videoSettings.selectedCasting;
    const selectedLocation = creativeBrief.selectedLocation || videoSettings.selectedLocation;

    // Get product images for reference
    const productImages = creativeBrief.selectedAssets || creativeBrief.crawledImages || [];
    const hasProductImages = productImages.length > 0;

    // Get brand colors - prioritize user's choices from brief, fall back to brand profile
    const brandColors = creativeBrief.brandColors || brand?.colors || [];
    const colorString = brandColors.length > 0
      ? brandColors.join(', ')
      : 'Not specified';

    const prompt = `You are a video director framing the OPENING SHOT of a ${videoSettings.videoLength || '6s'} video advertisement.

VIDEO CONTEXT:
- Product: ${creativeBrief.product || 'Not specified'}
- Target Audience: ${creativeBrief.targetAudience || 'Not specified'}
- Headline: ${creativeBrief.selectedHeadline || 'Not specified'}
- Video Style: ${videoSettings.videoStyle || 'commercial'}
- Desired Emotion: ${creativeBrief.desiredEmotion || 'Not specified'}
- CTA: ${creativeBrief.cta || 'Not specified'}
- Brand Colors: ${colorString}


${hasProductImages ? `PRODUCT: The ${creativeBrief.product} should be visible or prominently featured in the frame.` : ''}

${brandColors.length > 0 ? `BRAND COLORS: Use these colors strategically in the scene - for lighting accents, props, clothing, or environmental elements: ${colorString}` : ''}

Generate 4 DISTINCT first frame options. The first frame should:
1. Stop the scroll - be visually striking and attention-grabbing
2. Set up the story - hint at what's coming
3. Feature the character naturally in the location
4. Create curiosity or emotional connection
5. Be suitable as the opening of a short-form video ad

Vary the options by:
- Different camera angles (close-up vs medium vs wide)
- Different character poses/expressions (action vs contemplation vs direct address)
- Different product integration (subtle vs prominent)
- Different emotional hooks (curiosity vs excitement vs relatability)

For each option provide:
1. A shot name/type
2. A descriptive title
3. A detailed description of what we see
4. An IMAGE PROMPT for generating the frame

CRITICAL FIRST FRAME REQUIREMENTS for imagePrompt:
- Cinematic quality, suitable as video frame
- Character naturally placed in the location
- Professional lighting matching the location
- Product visible if applicable and naturally placed with the correct positioning and scale
- Dynamic composition that draws the eye
- Incorporate brand colors naturally through props, clothing, lighting, or environment
- NO text overlays - pure visual
- NO watermarks or logos in the image itself
- 9:16 vertical aspect ratio composition for social media

IMPORTANT:
 - Image must have a hook to catch the eye and engage viewers
 - Image must be visually striking and attention-grabbing
 - Image must be suitable as the opening of a short-form video ad

Return as JSON array:
[
  {
    "id": 1,
    "name": "Shot type (e.g., 'The Reveal', 'Morning Routine')",
    "title": "Descriptive title (e.g., 'Confident Entry')",
    "description": "Detailed description of the frame - character position, expression, product placement, lighting, mood, and how brand colors are incorporated",
    "imagePrompt": "Cinematic video frame of [character description] in [location description], [pose/action], [product integration], [brand color elements], [lighting], [composition notes], professional video quality, 9:16 vertical format, no text overlays."
  }
]`;

    try {
      // Use higher token limit for frame generation (4 detailed options need more space)
      const response = await this.callGeminiAPI(prompt, { maxOutputTokens: 4096 });
      console.log('[ConversationAgentV2] Raw first frame response:', response?.substring(0, 500));

      let text = response.replace(/```json/g, '').replace(/```/g, '').trim();

      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        try {
          const parsed = JSON.parse(jsonMatch[0]);
          console.log('[ConversationAgentV2] Generated first frame prompts:', parsed);
          return parsed;
        } catch (parseError) {
          console.error('[ConversationAgentV2] JSON parse error:', parseError);
          console.error('[ConversationAgentV2] Attempted to parse:', jsonMatch[0]?.substring(0, 500));
          throw new Error(`Failed to parse JSON: ${parseError.message}`);
        }
      }

      console.error('[ConversationAgentV2] No JSON array found in response:', text?.substring(0, 500));
      throw new Error('No valid JSON array found in AI response');
    } catch (error) {
      console.error('[ConversationAgentV2] Error generating first frame prompts:', error);
      throw error;
    }
  }

  /**
   * Generate 4 distinct last frame options for video ads
   * Creates closing shots with logo, CTA, and text elements
   * @param {object} creativeBrief - Current creative brief
   * @param {object} brand - Brand information
   * @returns {Array} - Array of last frame options with image prompts
   */
  async generateLastFramePrompts(creativeBrief, brand) {
    const videoSettings = creativeBrief.videoSettings || {};
    const selectedCasting = creativeBrief.selectedCasting || videoSettings.selectedCasting;
    const selectedLocation = creativeBrief.selectedLocation || videoSettings.selectedLocation;
    const firstFrame = videoSettings.firstFrame;

    // Get brand colors - prioritize user's choices from brief, fall back to brand profile
    const brandColors = creativeBrief.brandColors || brand?.colors || [];
    const colorString = brandColors.length > 0
      ? brandColors.join(', ')
      : 'Not specified';

    const prompt = `You are a video director framing the CLOSING SHOT of a ${videoSettings.videoLength || '6s'} video advertisement.

VIDEO CONTEXT:
- Product: ${creativeBrief.product || 'Not specified'}
- Brand: ${brand?.name || 'Not specified'}
- Headline: ${creativeBrief.selectedHeadline || 'Not specified'}
- CTA: ${creativeBrief.cta || 'Not specified'}
- Video Style: ${videoSettings.videoStyle || 'commercial'}
- Desired Emotion: ${creativeBrief.desiredEmotion || 'Not specified'}
- Brand Colors: ${colorString}

SELECTED CHARACTER:
${selectedCasting ? `- Name: ${selectedCasting.name || selectedCasting.title}
- Description: ${selectedCasting.description}` : '- No specific character'}

SELECTED LOCATION:
${selectedLocation ? `- Location: ${selectedLocation.name || selectedLocation.title}` : '- Studio/lifestyle setting'}

FIRST FRAME CONTEXT:
${firstFrame ? `- Opening shot: ${firstFrame.description || firstFrame.title}` : '- Standard opening'}

${brandColors.length > 0 ? `BRAND COLORS: These colors should be featured prominently in the closing frame for brand recognition: ${colorString}. Use them in backgrounds, lighting, or graphic elements.` : ''}

The last frame should:
1. Provide a satisfying conclusion to the story
2. Include the brand logo prominently (center or bottom)
3. Include the headline and key message as text overlays IN BRAND COLORS
4. Reinforce the emotional message
5. Be memorable and drive action

Vary the options by:
- Different character expressions (satisfied, inviting, triumphant)
- Different product prominence (hero shot vs lifestyle)
- Different compositions and text placements
- Different energy levels (calm resolution vs excited call-to-action)

For each option provide:
1. A shot name/type
2. A descriptive title
3. A detailed description including where text and logo are placed
4. An IMAGE PROMPT for generating the frame WITH integrated branding
5. Suggested text elements positions

CRITICAL LAST FRAME REQUIREMENTS for imagePrompt:
- Cinematic quality matching the first frame
- INCLUDE THE BRAND LOGO prominently in the frame
- INCLUDE TEXT OVERLAYS: The headline and key message should be rendered IN THE IMAGE using brand colors
- Product visible and prominent
- Professional lighting
- Brand colors (${colorString}) MUST be used for all text elements
- All text should be bold, modern, readable, and professionally integrated
- Satisfying, conclusive composition
- 9:16 vertical format for social media

Return as JSON array:
[
  {
    "id": 1,
    "name": "Shot type (e.g., 'The Hero Close', 'Satisfied Customer')",
    "title": "Descriptive title",
    "description": "Detailed description of the frame including text/logo placement and brand color usage",
    "imagePrompt": "Cinematic video closing frame of [scene description], [character if applicable], [product placement], with BOLD TEXT OVERLAY showing '[headline]' in [brand colors], '[key message]' below in [brand colors], brand logo positioned [location], professional lighting, 9:16 vertical format, text integrated into composition.",
    "textElements": {
      "logoPlacement": "center or bottom-center",
      "headlinePlacement": "upper-third or center",
      "keyMessagePlacement": "below headline",
      "suggestedCta": "${creativeBrief.cta || ''}"
    }
  }
]`;

    try {
      // Use higher token limit to ensure full JSON response for 4 frame options
      const response = await this.callGeminiAPI(prompt, { maxOutputTokens: 4096 });
      console.log('[ConversationAgentV2] Last frame raw response length:', response?.length);

      let text = response.replace(/```json/g, '').replace(/```/g, '').trim();

      const jsonMatch = text.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        console.log('[ConversationAgentV2] Generated last frame prompts:', parsed);
        return parsed;
      }

      console.error('[ConversationAgentV2] Last frame response (no JSON found):', text.substring(0, 500));
      throw new Error('No valid JSON array found in AI response');
    } catch (error) {
      console.error('[ConversationAgentV2] Error generating last frame prompts:', error);
      throw error;
    }
  }

  /**
   * Generate story arc scenarios for carousel based on the selected structure type
   * @param {Object} params - Parameters for scenario generation
   * @param {string} params.storyArcType - The selected story arc type (problem_solution, before_after, etc.)
   * @param {string} params.product - Product/service name
   * @param {string} params.audience - Target audience
   * @param {string} params.emotionalHook - Desired emotional hook
   * @param {string} params.marketingAngle - Marketing angle
   * @param {string} params.headline - Optional headline
   * @param {number} params.cardCount - Number of carousel cards
   * @returns {Array} - Array of story scenarios with card content
   */
  async generateStoryArcScenarios({
    storyArcType,
    product,
    audience,
    emotionalHook,
    marketingAngle,
    headline,
    cardCount = 4,
    productDetails = {}
  }) {
    const arcTypeDescriptions = {
      'problem_solution': 'Problem → Solution: Start with a relatable problem, build tension, then reveal the solution',
      'before_after': 'Before → After: Show the transformation journey from struggle to success',
      'feature_showcase': 'Feature Showcase: Highlight different features/benefits across cards',
      'educational_steps': 'Educational Steps: Teach something valuable in a step-by-step format',
      'testimonial_journey': 'Testimonial Journey: Build social proof through customer experience'
    };

    // Build product context from URL scan or brand details
    const productContext = [];
    if (productDetails.description) {
      productContext.push(`Product Description: ${productDetails.description}`);
    }
    if (productDetails.features && productDetails.features.length > 0) {
      productContext.push(`Key Features: ${Array.isArray(productDetails.features) ? productDetails.features.join(', ') : productDetails.features}`);
    }
    if (productDetails.sellingPoints && productDetails.sellingPoints.length > 0) {
      productContext.push(`Selling Points/Benefits: ${Array.isArray(productDetails.sellingPoints) ? productDetails.sellingPoints.join(', ') : productDetails.sellingPoints}`);
    }
    if (productDetails.uniqueValue) {
      productContext.push(`Unique Value Proposition: ${productDetails.uniqueValue}`);
    }

    const productContextStr = productContext.length > 0
      ? `\n\nPRODUCT DETAILS (USE THESE IN YOUR SCENARIOS):\n${productContext.join('\n')}`
      : '';

    const prompt = `You are a creative strategist generating carousel story scenarios.

CONTEXT:
- Product/Service: ${product}
- Target Audience: ${audience}
- Emotional Hook: ${emotionalHook || 'Not specified'}
- Marketing Angle: ${marketingAngle || 'Not specified'}
- Headline (if any): ${headline || 'None'}
- Story Arc Type: ${arcTypeDescriptions[storyArcType] || storyArcType}
- Number of Cards: ${cardCount}
${productContextStr}

Generate 3 DISTINCT story scenarios that follow the "${storyArcType}" structure. Each scenario should tell a complete story across ${cardCount} carousel cards.

CRITICAL REQUIREMENTS:
- USE THE ACTUAL PRODUCT FEATURES AND BENEFITS in your scenarios - don't use generic placeholder text
- Each scenario should highlight DIFFERENT aspects of the product
- Headlines must be SPECIFIC to "${product}" - mention actual features, benefits, or use cases
- For Problem→Solution: The "problem" should be a REAL pain point the audience faces, and the "solution" should mention SPECIFIC product capabilities

For each scenario, provide:
1. A catchy title that summarizes the story approach
2. A brief description of the overall narrative
3. The specific headline/text for EACH card in the carousel

IMPORTANT:
- Make the content SPECIFIC to the product "${product}" and audience "${audience}"
- Each card's headline should be punchy, engaging, and fit the story arc position
- Card 1 is always the hook (grab attention)
- Final card should have a clear CTA
- Middle cards should build the narrative
- USE REAL PRODUCT DETAILS, NOT GENERIC TEXT LIKE "The Problem" or "The Solution"

Respond with valid JSON:
{
  "scenarios": [
    {
      "id": "scenario_1",
      "title": "Catchy scenario title",
      "description": "Brief description of the overall story approach",
      "storyArcType": "${storyArcType}",
      "cards": [
        {
          "position": 1,
          "role": "hook",
          "headline": "Attention-grabbing headline for card 1",
          "subtext": "Supporting text or hook elaboration"
        },
        {
          "position": 2,
          "role": "build",
          "headline": "Headline for card 2",
          "subtext": "Supporting text"
        },
        // ... more cards up to ${cardCount}
        {
          "position": ${cardCount},
          "role": "cta",
          "headline": "CTA headline",
          "subtext": "Final call to action text"
        }
      ]
    },
    // ... 2 more scenarios
  ]
}`;

    try {
      const response = await this.callGeminiAPI(prompt, { maxOutputTokens: 4096 });
      console.log('[ConversationAgentV2] Story arc scenarios raw response length:', response?.length);

      let text = response.replace(/```json/g, '').replace(/```/g, '').trim();

      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        console.log('[ConversationAgentV2] Generated story arc scenarios:', parsed.scenarios?.length);
        return parsed.scenarios || [];
      }

      console.error('[ConversationAgentV2] Story arc response (no JSON found):', text.substring(0, 500));
      throw new Error('No valid JSON found in AI response');
    } catch (error) {
      console.error('[ConversationAgentV2] Error generating story arc scenarios:', error);
      throw error;
    }
  }

  /**
   * Generate a custom scenario from user's free text input
   * Takes user's description and structures it into carousel cards
   * @param {Object} params - Parameters for custom scenario
   * @param {string} params.userInput - User's description of their scenario
   * @param {string} params.storyArcType - The story arc structure type
   * @param {number} params.cardCount - Number of carousel cards
   * @param {string} params.product - Product/service name
   * @param {string} params.audience - Target audience
   * @returns {Object} - Structured scenario with card content
   */
  async generateCustomScenario({
    userInput,
    storyArcType,
    cardCount = 4,
    product,
    audience
  }) {
    const arcTypeDescriptions = {
      'problem_solution': 'Problem → Solution: Start with a relatable problem, build tension, then reveal the solution',
      'before_after': 'Before → After: Show the transformation journey from struggle to success',
      'feature_showcase': 'Feature Showcase: Highlight different features/benefits across cards',
      'educational_steps': 'Educational Steps: Teach something valuable in a step-by-step format',
      'testimonial_journey': 'Testimonial Journey: Build social proof through customer experience'
    };

    const prompt = `You are a creative strategist helping a user structure their custom carousel story idea.

USER'S INPUT:
"${userInput}"

CONTEXT:
- Product/Service: ${product || 'Not specified'}
- Target Audience: ${audience || 'Not specified'}
- Story Arc Type: ${arcTypeDescriptions[storyArcType] || storyArcType}
- Number of Cards: ${cardCount}

Take the user's idea and structure it into a complete carousel story that follows the "${storyArcType}" structure across ${cardCount} cards.

REQUIREMENTS:
- Preserve the user's core idea and message
- Structure it to flow naturally across ${cardCount} cards
- Card 1 should hook the viewer
- Middle cards should build the narrative
- Final card should have a clear call-to-action
- Each headline should be punchy and engaging (5-10 words max)
- Subtext should support the headline (10-20 words)

Respond with valid JSON:
{
  "id": "custom_scenario",
  "title": "A catchy title summarizing the user's idea",
  "description": "Brief description of the overall story",
  "storyArcType": "${storyArcType}",
  "isCustom": true,
  "cards": [
    {
      "position": 1,
      "role": "hook",
      "headline": "Attention-grabbing headline for card 1",
      "subtext": "Supporting text"
    },
    {
      "position": 2,
      "role": "build",
      "headline": "Headline for card 2",
      "subtext": "Supporting text"
    },
    // ... more cards up to ${cardCount}
    {
      "position": ${cardCount},
      "role": "cta",
      "headline": "CTA headline",
      "subtext": "Call to action text"
    }
  ]
}`;

    try {
      const response = await this.callGeminiAPI(prompt, { maxOutputTokens: 2048 });
      console.log('[ConversationAgentV2] Custom scenario raw response:', response?.substring(0, 300));

      let text = response.replace(/```json/g, '').replace(/```/g, '').trim();

      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        console.log('[ConversationAgentV2] Generated custom scenario:', parsed.title);
        return parsed;
      }

      console.error('[ConversationAgentV2] Custom scenario response (no JSON found):', text.substring(0, 500));
      throw new Error('No valid JSON found in AI response');
    } catch (error) {
      console.error('[ConversationAgentV2] Error generating custom scenario:', error);
      throw error;
    }
  }

  // ============================================
  // A/B TEST VARIANT GENERATION
  // ============================================

  /**
   * Generate A/B test variants for a creative
   * @param {Object} creativeBrief - The base creative brief
   * @param {string} testElement - What to test: 'headline', 'visual', 'cta', 'copy', 'angle', 'multiple'
   * @param {number} variantCount - Number of variants to generate (including control)
   * @param {string} hypothesis - User's hypothesis for what might work better
   * @returns {Object} - { control, variants, testConfig }
   */
  async generateABVariants(creativeBrief, testElement, variantCount = 2, hypothesis = '') {
    const testElementDescriptions = {
      headline: 'Test different headlines while keeping visuals and copy the same',
      visual: 'Test different visual styles/compositions while keeping copy the same',
      cta: 'Test different calls-to-action while keeping everything else the same',
      copy: 'Test different body copy/primary text while keeping headline and visuals the same',
      angle: 'Test completely different marketing angles (significant creative differences)',
      multiple: 'Test multiple elements together (headline + CTA combinations)'
    };

    const prompt = `You are an A/B testing strategist for digital advertising. Generate ${variantCount} distinct variants for testing.

BASE CREATIVE BRIEF:
- Product: ${creativeBrief.product || 'Not specified'}
- Target Audience: ${creativeBrief.targetAudience || 'Not specified'}
- Marketing Angle: ${creativeBrief.marketingAngle || 'Not specified'}
- Current Headline: ${creativeBrief.selectedHeadline || 'Not specified'}
- Current CTA: ${creativeBrief.cta || 'Not specified'}
- Visual Style: ${creativeBrief.visualStyle || 'Not specified'}
- Desired Emotion: ${creativeBrief.desiredEmotion || 'Not specified'}
${this.funnelContext?.funnelStage ? `- Funnel Stage: ${this.funnelContext.funnelStage}` : ''}

TEST ELEMENT: ${testElement}
${testElementDescriptions[testElement] || 'Test variations of the specified element'}

${hypothesis ? `USER'S HYPOTHESIS: "${hypothesis}"` : ''}

CRITICAL REQUIREMENTS:
1. Variant A (Control) should be based on the current brief values
2. Each variant should test a MEANINGFUL difference, not just word swaps
3. Each variant needs a clear hypothesis about WHY it might perform better
4. Keep non-tested elements consistent across variants
5. Consider the target audience and what might resonate differently

For '${testElement}' testing specifically:
${testElement === 'headline' ? '- Test different emotional appeals, benefit angles, or curiosity hooks' : ''}
${testElement === 'visual' ? '- Test different compositions, color moods, or subject focus' : ''}
${testElement === 'cta' ? '- Test different urgency levels, action words, or value propositions' : ''}
${testElement === 'copy' ? '- Test different lengths, tones, or storytelling approaches' : ''}
${testElement === 'angle' ? '- Test fundamentally different marketing approaches' : ''}
${testElement === 'multiple' ? '- Create cohesive combinations that test multiple hypotheses' : ''}

Respond with JSON:
{
  "testElement": "${testElement}",
  "testDescription": "Brief description of what this test will reveal",
  "variants": [
    {
      "id": "A",
      "label": "Control",
      "isControl": true,
      "hypothesis": "Baseline performance - current approach",
      "changes": {
        // Only include fields being tested
        ${testElement === 'headline' || testElement === 'multiple' ? '"headline": "Current headline",' : ''}
        ${testElement === 'cta' || testElement === 'multiple' ? '"cta": "Current CTA",' : ''}
        ${testElement === 'copy' ? '"primaryText": "Current copy",' : ''}
        ${testElement === 'visual' ? '"visualDirection": "Current visual approach",' : ''}
        ${testElement === 'angle' ? '"marketingAngle": "Current angle",' : ''}
      },
      "expectedOutcome": "Baseline to compare against"
    },
    {
      "id": "B",
      "label": "Descriptive variant name",
      "isControl": false,
      "hypothesis": "This variant tests [specific change] because [reasoning]",
      "changes": {
        // Changed values for tested elements
      },
      "expectedOutcome": "What we expect to see if hypothesis is correct"
    }
    // Additional variants if variantCount > 2
  ],
  "recommendedMetrics": ["CTR", "CPA", "ROAS"],
  "minimumSampleSize": 1000,
  "recommendedDuration": "7-14 days"
}`;

    try {
      const response = await this.callGeminiAPI(prompt, { maxOutputTokens: 4096 });
      console.log('[ConversationAgentV2] A/B variants response length:', response?.length);

      let text = response.replace(/```json/g, '').replace(/```/g, '').trim();

      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        console.log('[ConversationAgentV2] Generated A/B variants:', parsed.variants?.length);
        return parsed;
      }

      console.error('[ConversationAgentV2] A/B variants response (no JSON found):', text.substring(0, 500));
      throw new Error('No valid JSON found in AI response');
    } catch (error) {
      console.error('[ConversationAgentV2] Error generating A/B variants:', error);
      throw error;
    }
  }

  /**
   * Generate variant-specific image prompts
   * Takes a base brief and variant changes, returns an image generation prompt
   */
  async generateVariantImagePrompt(baseBrief, variantChanges) {
    const mergedBrief = { ...baseBrief, ...variantChanges };

    const prompt = `Generate an image prompt for this ad creative variant:

Product: ${mergedBrief.product || 'Not specified'}
Headline: ${mergedBrief.headline || mergedBrief.selectedHeadline || 'Not specified'}
Visual Style: ${mergedBrief.visualStyle || 'modern, professional'}
Visual Direction: ${variantChanges.visualDirection || mergedBrief.visualStyle || 'Not specified'}
Scene Type: ${mergedBrief.sceneType || 'lifestyle'}
Photography Mood: ${mergedBrief.photographyMood || 'natural'}
${mergedBrief.selectedCasting ? `Casting: ${typeof mergedBrief.selectedCasting === 'object' ? mergedBrief.selectedCasting.description : mergedBrief.selectedCasting}` : ''}
${mergedBrief.selectedLocation ? `Location: ${typeof mergedBrief.selectedLocation === 'object' ? mergedBrief.selectedLocation.description : mergedBrief.selectedLocation}` : ''}

Create a detailed image generation prompt that captures the visual direction for this variant.
The prompt should be suitable for AI image generation (Midjourney/DALL-E style).
Include: composition, lighting, mood, color palette, subject positioning.
Do NOT include any text in the image.

Respond with ONLY the image prompt, no other text.`;

    try {
      const response = await this.callGeminiAPI(prompt, { maxOutputTokens: 500 });
      return response.trim();
    } catch (error) {
      console.error('[ConversationAgentV2] Error generating variant image prompt:', error);
      throw error;
    }
  }

  /**
   * Suggest which element to A/B test based on the creative brief
   * Analyzes the brief and recommends high-impact test opportunities
   */
  async suggestABTestOpportunities(creativeBrief) {
    const prompt = `As an A/B testing strategist, analyze this creative brief and suggest the highest-impact testing opportunities.

CREATIVE BRIEF:
- Product: ${creativeBrief.product || 'Not specified'}
- Target Audience: ${creativeBrief.targetAudience || 'Not specified'}
- Marketing Angle: ${creativeBrief.marketingAngle || 'Not specified'}
- Headline: ${creativeBrief.selectedHeadline || 'Not specified'}
- CTA: ${creativeBrief.cta || 'Not specified'}
- Visual Style: ${creativeBrief.visualStyle || 'Not specified'}
- Desired Emotion: ${creativeBrief.desiredEmotion || 'Not specified'}
${this.funnelContext?.funnelStage ? `- Funnel Stage: ${this.funnelContext.funnelStage}` : ''}

Based on this brief, suggest 3 A/B test opportunities ranked by potential impact.
Consider: audience, funnel stage, industry norms, and common conversion drivers.

Respond with JSON:
{
  "opportunities": [
    {
      "rank": 1,
      "testElement": "headline" | "visual" | "cta" | "copy" | "angle",
      "title": "Short test name",
      "description": "What to test and why",
      "hypothesis": "What we expect to learn",
      "potentialImpact": "high" | "medium" | "low",
      "effort": "low" | "medium" | "high",
      "suggestedVariantCount": 2 | 3
    }
  ],
  "recommendation": "Which test to run first and why"
}`;

    try {
      const response = await this.callGeminiAPI(prompt, { maxOutputTokens: 2048 });

      let text = response.replace(/```json/g, '').replace(/```/g, '').trim();

      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        console.log('[ConversationAgentV2] Generated A/B test opportunities:', parsed.opportunities?.length);
        return parsed;
      }

      throw new Error('No valid JSON found in AI response');
    } catch (error) {
      console.error('[ConversationAgentV2] Error suggesting A/B test opportunities:', error);
      throw error;
    }
  }
}

// A/B Test Element Types
export const AB_TEST_ELEMENTS = {
  headline: {
    label: 'Headline',
    description: 'Test different headlines while keeping visuals consistent',
    icon: 'Type'
  },
  visual: {
    label: 'Visual Style',
    description: 'Test different visual approaches or compositions',
    icon: 'Image'
  },
  cta: {
    label: 'Call to Action',
    description: 'Test different CTAs and button text',
    icon: 'MousePointer'
  },
  copy: {
    label: 'Body Copy',
    description: 'Test different primary text and messaging',
    icon: 'FileText'
  },
  angle: {
    label: 'Marketing Angle',
    description: 'Test fundamentally different creative approaches',
    icon: 'Compass'
  },
  multiple: {
    label: 'Multiple Elements',
    description: 'Test combinations of headline + CTA together',
    icon: 'Layers'
  }
};

// Export the funnel stage context for use in UI
export { FUNNEL_STAGE_CONTEXT };

export default ConversationAgentV2;
